import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Download, Package, ShoppingCart } from "lucide-react";
import { api, apiError, downloadCsv, money, STATUS } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import Layout from "@/components/Layout";
import { useTenant } from "@/context/TenantContext";
import { cacheGet, cacheKey, cacheSet } from "@/lib/cache";
import ItemDrawer from "@/components/ItemDrawer";
import { EmptyState, ItemCode, LoadingPanel, StatusBadge } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

function Group({ title, hint, rows, sym, onOpen, tone }) {
  if (!rows.length) return null;
  return (
    <div className="panel overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-line">
        <span
          className={cn(
            "w-2 h-2 rounded-full shrink-0",
            tone === "out" ? "bg-stock-out" : "bg-stock-low"
          )}
        />
        <div className="min-w-0">
          <div className="font-display text-[16px] font-semibold text-ink">{title}</div>
          <p className="text-[12.5px] text-muted2 mt-0.5">{hint}</p>
        </div>
        <span className="ml-auto font-mono text-[13px] text-muted2 tnum shrink-0">
          {rows.length}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[680px]">
          <thead>
            <tr className="border-b border-line bg-surface/60">
              {["Code", "Item", "Category", "On hand", "Unit cost", "Sold to date", ""].map(
                (h, i) => (
                  <th
                    key={h + i}
                    className={cn(
                      "t-head px-4 py-2.5 text-left",
                      ["On hand", "Unit cost", "Sold to date"].includes(h) && "text-right"
                    )}
                  >
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((i) => (
              <tr
                key={i.item_code}
                onClick={() => onOpen(i.item_code)}
                className="t-row cursor-pointer last:border-0"
              >
                <td className="t-cell">
                  <ItemCode code={i.item_code} />
                </td>
                <td className="t-cell font-medium max-w-[260px] truncate">{i.name}</td>
                <td className="t-cell text-muted2 whitespace-nowrap">{i.category}</td>
                <td
                  className={cn(
                    "t-cell num font-mono font-medium",
                    i.current_stock <= 0 ? "text-stock-out" : "text-stock-low"
                  )}
                >
                  {i.current_stock}
                </td>
                <td className="t-cell num text-muted2">{money(i.unit_cost, sym)}</td>
                <td className="t-cell num text-muted2">{i.sold_qty}</td>
                <td className="t-cell">
                  <StatusBadge status={i.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Reorder() {
  const { settings } = useSettings();
  const sym = settings.currency_symbol;
  const { tenantId } = useTenant();
  const CK = cacheKey("reorder", tenantId);
  const [rows, setRows] = useState(() => cacheGet(CK) || []);
  const [loading, setLoading] = useState(() => !cacheGet(CK));
  const [openCode, setOpenCode] = useState(null);

  // Ignore a reply that lands after the client has been switched — otherwise
  // one business's figures can appear under another's name.
  const currentKey = useRef(CK);
  useEffect(() => {
    currentKey.current = CK;
  }, [CK]);

  const load = useCallback(async () => {
    const askedFor = CK;
    if (!cacheGet(CK)) setLoading(true);
    try {
      const { data } = await api.get("/reorder");
      if (currentKey.current !== askedFor) return;
      setRows(data);
      cacheSet(CK, data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [CK]);

  useEffect(() => {
    load();
  }, [load]);

  const { out, low, restockCost } = useMemo(() => {
    const out = rows.filter((r) => r.status === STATUS.OUT);
    const low = rows.filter((r) => r.status === STATUS.LOW);
    // A rough budget: bring everything flagged back up to ten units.
    const restockCost = rows.reduce(
      (t, r) => t + Math.max(10 - r.current_stock, 0) * (r.unit_cost || 0),
      0
    );
    return { out, low, restockCost };
  }, [rows]);

  return (
    <Layout
      title="Reorder"
      subtitle={
        // Held back until the data arrives — otherwise it briefly
        // describes an empty page.
        loading ? undefined : rows.length
          ? `${out.length} out of stock · ${low.length} running low · about ${money(
              restockCost,
              sym
            )} to restock to 10 each`
          : "Nothing needs restocking"
      }
      action={
        rows.length > 0 && (
          <Button
            variant="outline"
            onClick={() => downloadCsv("/export/reorder.csv", "reorder-list.csv")}
          >
            <Download className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Export shopping list</span>
          </Button>
        )
      }
    >
      {loading ? (
        <LoadingPanel rows={6} />
      ) : rows.length === 0 ? (
        <div className="panel">
          <EmptyState
            icon={Package}
            title="Everything's well stocked"
            body={`No item has fallen to ${settings.low_stock_threshold} units or fewer. Change the threshold in Settings if you want an earlier warning.`}
          />
        </div>
      ) : (
        <div className="space-y-5">
          {out.length > 0 && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50/70 px-5 py-4 flex items-start gap-3">
              <AlertTriangle className="w-[18px] h-[18px] text-stock-out shrink-0 mt-0.5" />
              <div>
                <div className="text-[14px] font-medium text-ink">
                  {out.length} item{out.length === 1 ? " is" : "s are"} completely out
                </div>
                <p className="text-[13px] text-muted2 mt-0.5">
                  These can't go into a gift box or a sale until you log a purchase.
                </p>
              </div>
            </div>
          )}

          <Group
            title="Out of stock"
            hint="Nothing left on the shelf"
            rows={out}
            sym={sym}
            onOpen={setOpenCode}
            tone="out"
          />
          <Group
            title="Running low"
            hint={`At or below ${settings.low_stock_threshold} units`}
            rows={low}
            sym={sym}
            onOpen={setOpenCode}
            tone="low"
          />

          <div className="panel panel-pad flex flex-col sm:flex-row sm:items-center gap-4">
            <ShoppingCart className="w-5 h-5 text-brand shrink-0" />
            <div className="flex-1">
              <div className="text-[14px] font-medium text-ink">Ready to buy?</div>
              <p className="text-[13px] text-muted2 mt-0.5">
                Export the list to take shopping, then log what you bought so the counts
                update.
              </p>
            </div>
            <Button asChild className="bg-brand hover:bg-brand-hover shrink-0">
              <a href="/purchases">Log a purchase</a>
            </Button>
          </div>
        </div>
      )}

      <ItemDrawer
        itemCode={openCode}
        open={!!openCode}
        onOpenChange={(v) => !v && setOpenCode(null)}
        onChanged={load}
      />
    </Layout>
  );
}
